import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import useUser from '../../hooks/useUser';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import { pushEvent } from '../../utils/analytics';
import Reveal from '../../components/course/Reveal';
import FactStrip from '../../components/course/FactStrip';
import PreviewVideo from '../../components/course/PreviewVideo';
import ScrollProgress from '../../components/course/ScrollProgress';
import { NO_CODING_BADGE } from '../../components/course/courseContent';
import '../courses/CoursePage.css';

const LIVE_COURSE_PATH = '/courses/ai-trading-bootcamp';

/**
 * Free challenge landing page and, once registered, the participant hub.
 *
 * Both states live at the same URL. Sessions come from the course /
 * session-group / course-session tables, so a new challenge run is new rows
 * rather than a new page.
 */

const DAILY_PLAN = [
  { day: 'Day 1', task: 'Install Claude Code or Codex and get your first working prompt' },
  { day: 'Day 2', task: 'Make the dashboard show the tickers you actually own' },
  { day: 'Day 3', task: 'Pull in live prices and recent news' },
  { day: 'Day 4', task: 'Record why you own each position, in your own words' },
  { day: 'Day 5', task: 'Make it report what changed against that reasoning, with sources' },
  { day: 'Day 6', task: 'Add risk flags and make the whole thing repeatable' },
  { day: 'Day 7', task: 'Screenshot it and send it in for demo day' },
];

const STEPS = [
  { title: 'Live kickoff', body: 'Rob sets up an AI trading workspace from scratch on camera and builds the first version of the dashboard live. You follow along.' },
  { title: 'Six short assignments', body: 'One email a day, each with a single 15 to 30 minute step. No lectures, just the next thing to do.' },
  { title: 'Live demo day', body: 'Rob reviews what people built, unsticks anyone who got stuck, and shows what to do next.' },
];

const FAQS = [
  { q: 'Is this really free?', a: 'Yes. Both live sessions and all six daily assignments are free.' },
  { q: 'Do I need to know how to code?', a: 'No. You will be directing Claude Code or Codex in plain English. That is the whole point.' },
  { q: 'What do I need before the kickoff?', a: 'Claude Code or Codex installed and signed in, and a list of a few tickers you actually care about.' },
  { q: 'What if I miss the live session?', a: 'Both sessions are recorded and sent to everyone who registered.' },
  { q: 'Is this a sales pitch?', a: 'You will build something real and useful in both sessions. Rob explains the paid bootcamp at the end of demo day. That is it.' },
];

const ChallengePage = () => {
  const { slug } = useParams();
  const { user } = useUser();
  const [challenge, setChallenge] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [registered, setRegistered] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', question: '', smsOptIn: false });

  useEffect(() => {
    let mounted = true;
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';
    (async () => {
      try {
        // The platform already models challenges as session groups, so we use the
        // real course API rather than a challenge-specific endpoint. The slug is
        // matched against the session group name.
        const listRes = await fetch(`${API_URL}/courses/session-groups`);
        if (!listRes.ok) throw new Error(String(listRes.status));
        const listData = await listRes.json();
        const groups = Array.isArray(listData) ? listData : (listData.sessionGroups || listData.data || []);

        const wanted = String(slug).toLowerCase().replace(/[^a-z0-9]+/g, '-');
        const match = groups.find((g) => {
          const asSlug = String(g.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
          return asSlug === wanted || asSlug.includes(wanted) || wanted.includes(asSlug);
        });
        if (!match) throw new Error('challenge_not_found');

        // The list response has no sessions; fetch the group for its schedule.
        const detailRes = await fetch(`${API_URL}/courses/session-groups/${match.id}`);
        const detail = detailRes.ok ? await detailRes.json() : match;
        if (!mounted) return;
        setChallenge({ ...match, ...detail });
        setRegistered(Boolean(detail?.isRegistered));
      } catch {
        /* Page still renders its evergreen state without backend data. */
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  useEffect(() => {
    if (user?.email) setForm((f) => (f.email ? f : { ...f, email: user.email }));
  }, [user]);

  const sessions = useMemo(
    () => (challenge?.sessions || []).slice().sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt)),
    [challenge]
  );
  const kickoff = sessions[0] || null;
  const demoDay = sessions[sessions.length - 1] || null;

  const fmt = useCallback((value) => (
    value ? new Date(value).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', timeZone: 'America/New_York',
    }) : null
  ), []);

  const register = useCallback(async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';
    try {
      if (!challenge?.id) throw new Error('challenge_not_loaded');
      // Guest registration: the route is validateTokenOptional, so no account
      // is required to save a seat.
      const res = await fetch(`${API_URL}/courses/session-groups/${challenge.id}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          notes: form.question || undefined,
          smsOptIn: form.smsOptIn,
        }),
      });
      if (!res.ok) throw new Error('registration_failed');
      setRegistered(true);
      pushEvent({ event: 'challenge_registered', source: window.location.pathname, slug });
    } catch {
      setError('That did not go through. Please try again, or email us and we will add you manually.');
    } finally {
      setSubmitting(false);
    }
  }, [form, slug, challenge]);

  const facts = useMemo(() => ([
    { value: kickoff ? fmt(kickoff.scheduledAt) : 'Announced soon', label: 'Live kickoff' },
    { number: 7, label: 'Days' },
    { value: 'Free', label: 'Price' },
    { value: demoDay ? fmt(demoDay.scheduledAt) : 'One week later', label: 'Demo day' },
  ]), [kickoff, demoDay, fmt]);

  const title = challenge?.name || 'Build Your Own AI Stock Dashboard in 7 Days';

  return (
    <div className="course-page" data-testid="challenge-page">
      <ScrollProgress />
      <Helmet>
        <title>{`${title} | Free Challenge | BotSpot`}</title>
        <meta name="description" content="A free 7-day challenge: build your own AI stock dashboard with Claude Code, live with Rob. No coding experience required." />
      </Helmet>

      <header className="cp-hero">
        <div className="cp-hero-copy">
          <Reveal><p className="cp-eyebrow">Free challenge · Live with Rob</p></Reveal>
          <Reveal delay={60}><h1>{title}</h1></Reveal>
          <Reveal delay={120}>
            <p className="cp-sub">
              Build a dashboard for the stocks you actually own, that tells you what changed and why.
              No coding experience required. You will be directing Claude Code in plain English.
            </p>
          </Reveal>

          {registered ? (
            <Reveal delay={200}>
              <div className="cp-pricing-card" data-testid="challenge-registered">
                <h3>You are in.</h3>
                <p>
                  Check your email for the calendar invite.
                  {kickoff ? ` The kickoff is ${fmt(kickoff.scheduledAt)} at 7:00 PM ET.` : ''}
                </p>
                {kickoff?.joinUrl ? (
                  <a className="cp-btn-primary" href={kickoff.joinUrl} target="_blank" rel="noopener noreferrer">
                    Join the live session
                  </a>
                ) : null}
                <p className="cp-note">
                  Before kickoff: install Claude Code or Codex, sign in, and pick a few tickers you care about.
                </p>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={200}>
              <form className="cp-form" onSubmit={register} data-testid="challenge-form">
                <label>
                  Your name
                  <input type="text" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </label>
                <label>
                  Email
                  <input type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </label>
                <label>
                  What would you like Rob to build live? (optional)
                  <input type="text" value={form.question}
                    onChange={(e) => setForm({ ...form, question: e.target.value })} />
                </label>
                <label className="cp-check">
                  <input type="checkbox" checked={form.smsOptIn}
                    onChange={(e) => setForm({ ...form, smsOptIn: e.target.checked })} />
                  <span>Text me a reminder before each live session. Message and data rates may apply. Reply STOP to opt out.</span>
                </label>
                <button type="submit" className="cp-btn-primary" disabled={submitting} data-testid="challenge-submit">
                  {submitting ? 'Saving your seat...' : 'Save my seat'}
                </button>
                <p className="ck-nocode">{NO_CODING_BADGE}</p>
                {error ? <p className="cp-note" role="alert">{error}</p> : null}
              </form>
            </Reveal>
          )}
        </div>

        <Reveal delay={160} className="cp-hero-art">
          <img src="/images/courses/ai-stock-dashboard.webp"
            alt="The AI stock dashboard you build: position cards with sparklines and a what-changed feed with sources"
            width="1280" height="714" loading="eager" />
        </Reveal>
      </header>

      <FactStrip facts={facts} />

      <section className="cp-section cp-day7">
        <Reveal><h2>What you will have by day 7</h2></Reveal>
        <Reveal delay={70}>
          <p className="cp-sub">
            Your own dashboard, for the stocks you actually own, that pulls live data and tells you
            what changed against the reasons you bought each position, with sources you can check.
          </p>
        </Reveal>
        <Reveal delay={140}>
          <img className="cp-day7-shot" src="/images/courses/ai-stock-dashboard.webp"
            alt="The finished dashboard: position rows with sparklines beside a what-changed feed"
            width="1280" height="714" loading="lazy" />
        </Reveal>
      </section>

      <PreviewVideo
        heading="See how Rob teaches"
        blurb="A clip from a real session. The challenge is taught the same way: live, from scratch, nothing skipped."
      />

      <section className="cp-section">
        <Reveal><h2>How it works</h2></Reveal>
        <ul className="cp-steps">
          {STEPS.map((s, i) => (
            <Reveal as="li" key={s.title} delay={i * 90}>
              <h3>{s.title}</h3><p>{s.body}</p>
            </Reveal>
          ))}
        </ul>
      </section>

      <section className="cp-section">
        <Reveal><h2>The seven days</h2></Reveal>
        <ul className="cp-timeline">
          {DAILY_PLAN.map((d, i) => (
            <Reveal as="li" key={d.day} delay={i * 40}>
              <span className="cp-day">{d.day}</span>
              <span className="cp-task">{d.task}</span>
            </Reveal>
          ))}
        </ul>
      </section>

      {sessions.some((s) => s.recordingUrl) && (
        <section className="cp-section">
          <Reveal><h2>Past sessions</h2></Reveal>
          <ul className="cp-archive">
            {sessions.filter((s) => s.recordingUrl).map((s) => (
              <Reveal as="li" key={s.id}>
                <strong>{s.title}</strong>
                <br />
                <a href={s.recordingUrl} target="_blank" rel="noopener noreferrer">Watch the recording</a>
              </Reveal>
            ))}
          </ul>
        </section>
      )}

      <section className="cp-section cp-faq">
        <Reveal><h2>Questions people ask</h2></Reveal>
        <dl>
          {FAQS.map((f, i) => (
            <Reveal as="div" key={f.q} delay={i * 40}><dt>{f.q}</dt><dd>{f.a}</dd></Reveal>
          ))}
        </dl>
      </section>

      <section className="cp-section cp-final">
        <Reveal>
          <h2>Want the full bootcamp?</h2>
          <p className="cp-muted">
            The challenge is week one. The bootcamp is all five weeks, live, including connecting your
            broker and placing reviewed trades through your AI.
          </p>
          <Link className="cp-btn-primary" to={LIVE_COURSE_PATH}>See the AI Trading Bootcamp</Link>
        </Reveal>
      </section>

      {isLoading ? null : null}

      <p className="cp-disclaimer">
        BotSpot is not an investment advisor. This is educational material about using AI tools in your
        own research and trading process. Nothing here is a recommendation to buy or sell any security,
        and no outcome or return is promised.
      </p>
    </div>
  );
};

export default ChallengePage;
